import { action, autorun, computed, IReactionDisposer, observable } from 'mobx';
import { Store } from './mainStore';
import errorMessages from '@/assets/error-messages.json';
import { packs } from '@/store/ProductStore';
import camelCase from 'camelcase';
import { WeightType } from '@/store/patientStore';
import { Patient } from '@/store/engine/openapi/generated';

export default class UtilsStore {
	@observable signOutModal = false;
	@observable timeoutError = false;
	@observable snackbarMessages = [] as { message: string; timestamp: number }[];
	@observable error = false;
	@observable inactivityLogout = false; // Logout after 28 minutes of inactivity
	@observable automaticLogout = false; // Automatic logout after 7 hours of HP session and not assisting
	@observable criticalAutomaticLogout = false; // Automatic logout after 8 hours of HP session
	@observable errorInfo = {
		description: '',
		details: ''
	};
	@observable messagesToIgnore: Array<string> = [];
	@observable displayMetadataWaitingPopup = false;
	@observable showSearchModal = false;
	@observable allowSearch = true;
	@observable savedRxFeatures = true;
	@observable newPrescription = true; // FIXME: Remove as never changes
	scanWatcher: IReactionDisposer | undefined;
	readonly timeout = 3500;
	store: Store;

	@observable showNotes = false;
	@observable patientModal = false;
	@observable addingPatient = false;
	@observable mobileAppView = false;
	static inputUnifiedRegex: RegExp = /[\x20-\x21\x23-\x26\x28-\x29\x2b\x2d-\x2e\x30-\x39\x40-\x5b\x5d-\x7b\x7d-\x7e\x80-\xfe]/g;
	static textUnifiedRegex: RegExp = /[\x20-\x21\x23-\x26\x28-\x29\x2b\x2d-\x2e\x30-\x39\x40-\x5b\x5d-\x7b\x7d-\x7e\x80-\xfe\r\n]/g;

	constructor(store: Store) {
		this.store = store;

		// Change left/right/pair value if necessary

		this.messagesToIgnore = Object.values(errorMessages.snackbars).map((m) => {
			return m;
		});
	}

	static isIsoDate(str) {
		if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(.\d{3}Z|Z)/.test(str)) return false;
		const d = new Date(str);
		return d.toISOString() === str || d.toISOString().replace('.000', '') === str;
	}

	static wait(ms: number) {
		return new Promise((p) => setTimeout(p, ms));
	}

	static base64ToArrayBuffer(base64) {
		let binary_string = window.atob(base64);
		let len = binary_string.length;
		let bytes = new Uint8Array(len);
		for (let i = 0; i < len; i++) {
			bytes[i] = binary_string.charCodeAt(i);
		}
		return bytes.buffer;
	}

	public static getOrderSession(patient) {
		const order = patient.sessionsStatuses.find((el) =>
			[
				sessionStatuses[sessionStatuses.length - 1],
				sessionStatuses[sessionStatuses.length - 2],
				sessionStatuses[sessionStatuses.length - 3],
				'SESSION_STATUS_REPAIR_ORDER_CREATED'
			].includes(el.status)
		); // Find order
		if (order !== undefined) {
			return order;
		}
	}
	public static getHighestPriorityStatus(patient: Patient) {
		let highestPriority: SessionStatus | '' = '';

		if (!patient.sessionsStatuses) return;

		for (const [i, status] of patient.sessionsStatuses.entries()) {
			if (status && status.status && status.status.includes('ORDER')) {
				if (i === 0) {
					highestPriority = status.status;
					return highestPriority;
				} else {
					if (sessionStatuses.indexOf(highestPriority) >= 1) {
						return (highestPriority = 'SESSION_STATUS_PRESCRIPTION_CREATED');
					}
					return (highestPriority = 'SESSION_STATUS_ORDER_CREATED');
				}
			} else {
				if (status && status.status && sessionStatuses.indexOf(status.status) > sessionStatuses.indexOf(highestPriority)) {
					highestPriority = status.status;
				}
			}
		}
		return highestPriority;
	}
	static camelCaseKeys(object) {
		if (typeof object != 'object') return object;
		if (object === null) return null;
		let newObj = { ...object };
		for (let oldName in newObj) {
			// Camel to underscore
			let newName = camelCase(oldName);

			// Only process if names are different
			if (newName != oldName) {
				// Check for the old property name to avoid a ReferenceError in strict mode.
				if (newObj.hasOwnProperty(oldName)) {
					newObj[newName] = newObj[oldName];
					delete newObj[oldName];
				}
			}

			// Recursion
			if (typeof newObj[newName] == 'object') {
				newObj[newName] = UtilsStore.camelCaseKeys(newObj[newName]);
			}
		}
		return newObj;
	}

	static capitalize(str: string) {
		return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
	}

	async getTimes() {
		const auth = await this.store.engine.auth;
		console.log('Token ending time ', new Date(auth.tokenExpiresAt));
		console.log('Token should refresh in ', (auth.tokenExpiresAt - new Date().getTime() - 60000) / 1000, 's');

		console.log('Session ends at ', new Date(auth.sessionEndsAt));
		console.log('Session should end at (soft - when on index screen) ', new Date(auth.sessionEndsAt - 3600000));
		console.log('Session should end in (soft - when on index screen) ', (auth.sessionEndsAt - new Date().getTime() - 3600000) / 1000, 's');

		console.log('Session should end at (critical - everywhere) ', new Date(auth.sessionEndsAt - 1800000));
		console.log('Session should end in (critical - everywhere) ', (auth.sessionEndsAt - new Date().getTime() - 1800000) / 1000, 's');
	}

	@computed get showButtons() {
		const pdfPages = ['/summary', '/prescription', '/end-view'];
		return {
			inspection: this.store.biometric.appropriateNumberOfScansExist && this.store._patient.userRecordID && this.store.currentRoute !== '/order-styles',
			pdf: !!(this.store._patient.userRecordID && pdfPages.includes(this.store.currentRoute)),
			addPatient: this.store.currentRoute === '/patients' && !!this.store.auth.chosenStore,
			search: this.store.currentRoute === '/patients' && this.store.auth.operator.loggedIn && this.allowSearch
		};
	}

	@computed get showAuthModal() {
		return !this.store.auth.operator.loggedIn && !this.store.auth.connecting && !this.error;
	}

	@computed get showPrivacyPolicyModal() {
		return this.store.currentRoute === '/privacy-policy';
	}

	@computed get showDownloadUsbAgentModal() {
		return this.store.currentRoute === '/usbagent';
	}

	@computed get showBlobsplash() {
		return !this.store.auth.chosenStore || this.store.currentRoute === '/index';
	}

	@computed get showPatientInfo() {
		return (
			this.store.currentRoute !== '/patients' &&
			this.store.currentRoute !== '/getting-started' &&
			this.store.currentRoute !== '/index' &&
			this.store.currentRoute !== '/saved-rx' &&
			this.store.currentRoute !== '/saved-rx-list' &&
			this.store.currentRoute !== '/privacy-policy' &&
			this.store.currentRoute !== '/usbagent' &&
			this.store.auth.chosenStore
		);
	}

	@action isIPad() {
		return (
			['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
			// iPad on iOS 13 detection
			(navigator.userAgent.includes('Mac') && 'ontouchend' in document)
		);
	}

	@action pushMessage(msg, force = false) {
		const timestamp = Date.now();

		if (process.env.NODE_ENV !== 'development' && !force && !this.messagesToIgnore.includes(msg)) {
			msg = errorMessages.tryAgain;
		}
		const message = msg.substr(0, 250) + (msg.length > 247 ? '...' : '');

		if (this.snackbarMessages.some((m) => m.message === message)) {
			return;
		}

		this.snackbarMessages.push({
			message,
			timestamp
		});

		setTimeout(() => (this.snackbarMessages = this.snackbarMessages.filter((_) => _.timestamp !== timestamp)), this.timeout + msg.length * 12.5);
	}

	static clamp(number, min, max) {
		return Math.max(min, Math.min(number, max));
	}

	getWeightInLBS(weight: WeightType, value: number) {
		return weight === 'kg' ? (+value * 2.2046).toFixed(0) : value;
	}

	public static unifiedValidatorsForInput(value) {
		if (!value) {
			return null;
		}
		const matches = value.match(this.inputUnifiedRegex);
		if (!matches) {
			return 'invalid';
		}

		const mValue = matches.join('');
		if (mValue !== value) {
			return 'invalid';
		}
		return null;
	}

	public static removeInvalidCharactersForInput(value) {
		if (!value) {
			return '';
		}

		const matches = value.match(this.inputUnifiedRegex);
		if (!matches) {
			return '';
		}

		return matches.join('');
	}

	public static unifiedValidatorsForTextarea(value) {
		if (!value) {
			return null;
		}
		const matches = value.match(this.textUnifiedRegex);
		if (!matches) {
			return 'invalid';
		}

		const mValue = matches.join('');
		if (mValue !== value) {
			return 'invalid';
		}
		return null;
	}

	public static removeInvalidCharactersForTextarea(value) {
		if (!value) {
			return '';
		}

		const matches = value.match(this.textUnifiedRegex);
		if (!matches) {
			return '';
		}

		return matches.join('');
	}
}
const sessionStatuses = [
	'SESSION_STATUS_SESSION_CREATED',
	'SESSION_STATUS_MEASUREMENT_DATA_CAPTURED',
	'SESSION_STATUS_PRESCRIPTION_CREATED',
	'SESSION_STATUS_PRESCRIPTION_COMPLETED',
	'SESSION_STATUS_PRESCRIPTION_UPDATED',
	'SESSION_STATUS_ORDER_DELIVERED',
	'SESSION_STATUS_ORDER_CANCELLED',
	'SESSION_STATUS_ORDER_CREATED'
];

type SessionStatus = typeof sessionStatuses[number];
